import React, { useEffect, useMemo } from 'react';
import { Radio } from 'antd';
import { useSelector } from 'react-redux';
import { t } from '../../../../../../services/i18n';
import { Colors } from '../../../../../../styles/Colors';
import { GlobalStyleAttributes } from '../../../../../../styles/GlobalStyles';
import VStack from '../../../../../common/Layout/VStack';
import HStack from '../../../../../common/Layout/HStack';
import AppText from '../../../../../common/AppText';
import UserAvatar from '../../../../../common/UserAvatar';
import HugBookingOptionsCheckboxes from '../../EventManage/HugBookingOptionsCheckboxes';
import useBookingOptions from '../../../../../../Hooks/useBookingOptions';
import { getHugFormattedDaysAndDate, getHugSeriesEventOptions, getBoxTimeFormat, getBookingOptionLabel } from '../../../../../../services/Helpers';
import _ from 'lodash';
import CustomButtonV2 from '../../../../../common/CustomButtonV2';
import { HUG_DAY_BOOKING } from '../../../../../../services/ConstHelper';
import { HUG_FULL_BOOKING, HUG_PARTIAL_BOOKING } from '../../../../../../services/ConstHelper';
import InfoTooltip from '../../../../../common/InfoTooltip';
import AdvanceSelect from '../../../../../common/AdvanceSelect';
import usePermissions from '../../../../../../services/Permissions';

const BookingOptionCard = ({ value, label, bookingType, onChange, disabled, children }) => {
    const isSelected = bookingType === value;

    const handleClick = (e) => {
        e.stopPropagation()
        !disabled && onChange(value)
    }
    return (
        <CustomButtonV2
        onClick={handleClick}
        iconProps={{iconColor: Colors.borderColor, iconHoverColor: !disabled && Colors.newDesign.mainBlue}}
        override={({color}) => (
            <VStack w={'100%'}>
                <HStack space={'0.5rem'} alignItems={'start'} p={'1.25rem'} opacity={disabled ? '50%' : '100%'}
                         style={{border: `1px solid ${isSelected ? Colors.newDesign.mainBlue : color}`,borderRadius: '4px'}}>
                    <Radio value={value} size="sm" disabled={disabled} onClick={handleClick} style={{margin: 0}}/>
                    <VStack flex={1} space={'0.5rem'}>
                        <AppText boldFont>{label}</AppText>
                        {isSelected && children}
                    </VStack>
                </HStack>
            </VStack>
            )} />
    );
};

const BookingType = ({ target, values, setFieldValue, setFieldTouched, touched, errors }) => {
    const user = target?.user;
    const eventData = values?.eventData;
    const displayPartialOptions = useMemo(() => eventData.series && eventData.series.partialBookingOptions?.length > 0 ? Object.values(_.groupBy(eventData.series.partialBookingOptions, 'numOfDays')).map(group => group[0]) : null, [eventData]);
    const { has } = usePermissions()
    const lang = useSelector(state => state.userSettings.lang);
    const box = useSelector(state => state.box.box);
    const cancelReasonOptions = box.boxCancelReasons.filter(reason => reason.active);
    const boxLocale = useMemo(() => lang?.language === 'Hebrew' ? 'he' : 'en', [lang]);
    const timeFormat = useMemo(() => box ? box.locations_box[0].time_format : null, [box]);
    const selectedNumOfDays = useMemo(() => values.bookingType.startsWith(HUG_PARTIAL_BOOKING) ? parseInt(values.bookingType.split('_').pop(), 10) : null, [values.bookingType]);

    const { seriesEvents } = useBookingOptions({
        selectedBookingType: values.bookingType,
        eventData,
    });

    useEffect(() => {
        if (!_.isEqual(seriesEvents, values.selectedSeriesEvents)) {
            setFieldValue('selectedSeriesEvents', seriesEvents);
        }
    }, [seriesEvents]);

    useEffect(() => {
        const defaultCancelReasonId = box?.settings?.defaults?.cancel_reason_id;
        if (!values.cancelReasonId && defaultCancelReasonId && cancelReasonOptions.some(reason => reason.id === defaultCancelReasonId)) {
            setFieldValue('cancelReasonId', defaultCancelReasonId);
        }
    }, []);


    const isPartialMode = useMemo(() =>
        ![HUG_FULL_BOOKING, HUG_DAY_BOOKING].includes(values.bookingType),
        [values.bookingType]
    );

    const isHugFull = eventData?.series?.isHugFull;
    const getSelectedOptionLabel = () => getBookingOptionLabel(isPartialMode ? selectedNumOfDays : null);
    const fullBookingOptionLabel = isHugFull ? `${getBookingOptionLabel(null)} ${t('hugFullBookingAllSessions')}` : getBookingOptionLabel(null);

    const onBookingTypeChange = (value) => {
        setFieldTouched('selectedSeriesEvents', false);
        setFieldTouched('cancelReasonId', false);
        setFieldValue('bookingType', value);
        setFieldValue('selectedSeriesEvents', null);
    };

    const { dateRange, daysAndHours } = useMemo(() => eventData
    ? getHugFormattedDaysAndDate({ eventData, bookingType: values.bookingType, selectedSeriesEvents:values.selectedSeriesEvents, boxLocale, timeFormat, HUG_FULL_BOOKING })
    : {}, [eventData, values.bookingType, values.selectedSeriesEvents])

    return (
            <VStack space={'1.5rem'}>
                <VStack space={'0.5rem'}>
                    <HStack alignItems={'center'}>
                        <AppText boldFont fontSize={GlobalStyleAttributes.fonts.heading2Desktop}>{t('cancellation-reason')}</AppText>
                        <InfoTooltip title={t('cancellation-reason-tooltip')} />
                    </HStack>
                    <AdvanceSelect placeholder={t('select')} options={cancelReasonOptions.map(reason => ({ label: reason.name, value: reason.id }))} onChange={(obj) => setFieldValue('cancelReasonId', obj.value, true)} initValue={values.cancelReasonId} isError={touched.cancelReasonId && errors.cancelReasonId}/>
                    {touched.cancelReasonId && errors.cancelReasonId && <AppText color={Colors.error} fontSize={GlobalStyleAttributes.fonts.infoText} width='max-content' mediumFont>
                    {t('thisFieldIsMandatory')}
                    </AppText>}
                </VStack>
                {displayPartialOptions &&<Radio.Group name={'booking-type-radio'} value={values.bookingType}>
                    <VStack space={'0.75rem'}>
                        <AppText boldFont fontSize={GlobalStyleAttributes.fonts.heading2Desktop}>{t('bookingOptions')}</AppText>
                        <VStack space={'0.5rem'}>
                            <BookingOptionCard
                                value={HUG_FULL_BOOKING}
                                label={fullBookingOptionLabel}
                                bookingType={values.bookingType}
                                onChange={onBookingTypeChange}
                            />
                            {has('managePartialBooking') && displayPartialOptions && displayPartialOptions.map(option => (
                                    <BookingOptionCard
                                        key={option.numOfDays}
                                        value={`${HUG_PARTIAL_BOOKING}_${option.numOfDays}`}
                                        label={getBookingOptionLabel(option.numOfDays)}
                                        bookingType={values.bookingType}
                                        onChange={onBookingTypeChange}
                                        disabled={!option.canBook}
                                    >
                                        <HugBookingOptionsCheckboxes
                                            eventData={eventData}
                                            bookingType={values.bookingType}
                                            showError={touched.selectedSeriesEvents && errors.selectedSeriesEvents}
                                            onSelectSeriesEvents={(value) => setFieldValue('selectedSeriesEvents', value, true)}
                                            selectedSeriesEvents={values.selectedSeriesEvents}
                                        />
                                    </BookingOptionCard>
                            ))}
                        </VStack>
                    </VStack>
                </Radio.Group>}


                <VStack space={'0.75rem'}>
                    <AppText boldFont fontSize={GlobalStyleAttributes.fonts.heading3}>{t('bookingDetails')}</AppText>
                    <div style={{ border: `1px solid ${Colors.newDesign.mediumGrey}`, borderRadius: 8, padding: '16px' }}>
                        <VStack space={'0.75rem'}>
                            {user && (
                                <HStack space={'0.75rem'} alignItems={'center'}>
                                    <UserAvatar
                                        imageUri={user.image}
                                        firstName={user.firstName}
                                        lastName={user.lastName}
                                    />
                                    <AppText mediumFont>{user.firstName} {user.lastName}</AppText>
                                </HStack>
                            )}
                            <VStack space={'0.25rem'}>
                                <HStack space={'0.25rem'} alignItems={'center'}>
                                    <AppText mediumFont>{t('booking-type-label')}:</AppText>
                                    <AppText>{getSelectedOptionLabel()}</AppText>
                                    {values.bookingType === HUG_FULL_BOOKING && eventData.series.isHugFull && <AppText>{t('hugFullBookingAllSessions')}</AppText>}
                                </HStack>
                                {dateRange && (
                                    <HStack space={'0.25rem'} alignItems={'center'}>
                                        <AppText mediumFont>{t('date-of-hug-label')}:</AppText>
                                        <AppText>{dateRange}</AppText>
                                        {eventData?.series?.has_started && <AppText>{t('course-started')}</AppText>}
                                    </HStack>
                                )}
                                {daysAndHours && (
                                    <VStack space={'0'}>
                                        <AppText mediumFont>{t('days-label')}:</AppText>
                                        <AppText>{daysAndHours}</AppText>
                                    </VStack>
                                )}
                            </VStack>
                        </VStack>
                    </div>
                </VStack>
            </VStack>
    );
};

export default BookingType;
